/// <amd-module name="VSS/Platform/Components/Layout/Layout" />
import React = require("react");
import { css, IVssComponentProperties, RegionComponent, VssComponent, WrappedComponent } from "../../Layout";
import { ErrorBoundary } from "../ErrorBoundary/ErrorBoundary";
import { IHtmlElementComponentProperties, ILayoutComponentProperties } from "./Layout.Props";

/**
 * A layout component is used to create a custom layout of a series of sub-components.
 *
 * Examples are things like the overall page layout with the header/content/footer.
 * The layout component is based on a flex element and gives the caller a great deal
 * of flexibility with how the components are laid out. There are basic layouts
 * the component can handle by default but it allows the caller to specify their
 * styling. This means things like media queries can be used to change the general
 * layout without having to have mulitple renderings of the internal components.
 */
export class LayoutComponent<P extends ILayoutComponentProperties> extends VssComponent<P, {}> {
    public static componentType = "layout";

    public render(): JSX.Element {
        return React.createElement(
            this.props.elementType || "div",
            {
                "aria-label": this.props["aria-label"],
                className: css(
                    this.props.orientation && (this.props.orientation as string) === "column" ? "flex-column" : "flex-row",
                    this.props.className
                )
            },

            // Render each component into the layout, each component is given a container
            // element to render into within the layout component.
            this.props.components.map((subComponent, index) => {
                // If a componentType is defined, it is responsible for rendering the layout element.
                // If not then a componentRegion should have been specified and we will render the
                // region into the layout element using the layout manager.
                let subComponentElement: React.ReactNode;
                if (subComponent.componentType) {
                    subComponentElement = React.createElement(WrappedComponent, {
                        key: index,
                        hasBoundary: !!subComponent.errorBoundaryProps,
                        ...subComponent.componentProperties,
                        wrappedType: subComponent.componentType
                    });
                } else if (subComponent.componentRegion) {
                    subComponentElement = React.createElement(RegionComponent, {
                        key: index,
                        hasBoundary: !!subComponent.errorBoundaryProps,
                        ...subComponent.componentProperties,
                        componentRegion: subComponent.componentRegion
                    });
                }

                // Attach to an error boundary if necessary
                if (subComponent.errorBoundaryProps) {
                    return React.createElement(ErrorBoundary, { key: index, ...subComponent.errorBoundaryProps }, subComponentElement);
                } else {
                    return subComponentElement;
                }
            })
        );
    }
}

// Register the layout component with the layout manager
VssComponent.register(LayoutComponent.componentType, LayoutComponent);

/**
 * The layout filler component produces a single element that acts as a "filler" within
 * a flexbox. It sets up the flex-grow to be 1 which forces it to take all available
 * space in the flex container that is not used by the auto layout of another item.
 */
export class LayoutFillerComponent<P extends IVssComponentProperties> extends VssComponent<P, {}> {
    public static componentType = "layoutFiller";

    public render(): JSX.Element {
        return React.createElement("div", { className: css("flex-grow", this.props.className) });
    }
}

// Register the layout filler with the layout manager
VssComponent.register(LayoutFillerComponent.componentType, LayoutFillerComponent);

/**
 * The html element component produces a single element.
 */
export class HtmlElementComponent<P extends IHtmlElementComponentProperties> extends VssComponent<P, {}> {
    public static componentType = "htmlElement";

    public render(): JSX.Element {
        return React.createElement(this.props.elementType || "div", { className: this.props.className }, this.props.text);
    }
}

// Register the HtmlElement component with the layout manager.
VssComponent.register(HtmlElementComponent.componentType, HtmlElementComponent);

/**
 * NoOpComponent is a commponent that doesnt render anything.
 * It is useful when a component is needed but no actual behavior is required.
 */
export class NoOpComponent<P extends IVssComponentProperties> extends React.Component<P, {}> {
    public static componentType = "NoOp";

    public render(): null {
        return null;
    }
}

VssComponent.register(NoOpComponent.componentType, NoOpComponent);
